import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  computed,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { SolButtonType, SolText, TranslatePipe } from '@sol/components';
import { SolIcon } from '@sol/icons';
import { LabButtonComponent, type LabButtonMood } from '../lab-button/lab-button.component';
import { LabIconComponent } from '../lab-icon/lab-icon.component';
import { coerceLabSolText } from '../../tools/lab-sol-text-input';
import { LabAction, LabActionTools } from '../../tools/lab-action';

export type LabBannerMood = 'info' | 'success' | 'warning' | 'danger';

@Component({
  selector: 'lab-banner',
  standalone: true,
  imports: [LabButtonComponent, LabIconComponent, TranslatePipe],
  templateUrl: './lab-banner.component.html',
  styleUrl: './lab-banner.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LabBannerComponent implements OnDestroy {
  private readonly renderedState = signal(false);
  private readonly openState = signal(false);
  private readonly headingState = signal<SolText>(SolText.empty());
  private readonly descriptionState = signal<SolText>(SolText.empty());
  private readonly actionState = signal<LabAction | undefined>(undefined);
  private readonly secondaryActionState = signal<LabAction | undefined>(undefined);

  @HostBinding('attr.title')
  public readonly hostTitle: null = null;

  @Input() mood: LabBannerMood = 'info';
  // Renamed from `title` so the API doesn't shadow the native `title`
  // attribute. No sol counterpart yet — when sol-banner ships, mirror this name.
  @Input() dismissible = true;

  @Input({ transform: coerceLabSolText })
  public set heading(value: SolText) {
    this.headingState.set(value);
  }

  @Input({ transform: coerceLabSolText })
  public set description(value: SolText) {
    this.descriptionState.set(value);
  }

  @Input()
  public set action(action: LabAction | undefined) {
    this.actionState.set(action);
  }

  @Input()
  public set secondaryAction(action: LabAction | undefined) {
    this.secondaryActionState.set(action);
  }

  @Input()
  public set visible(value: boolean) {
    this.setOpen(Boolean(value));
  }

  @Output('action') readonly actionEmitter = new EventEmitter<LabAction>();
  @Output('secondaryAction') readonly secondaryActionEmitter = new EventEmitter<LabAction>();
  @Output() readonly visibleChange = new EventEmitter<boolean>();

  protected readonly isRendered$ = this.renderedState.asReadonly();
  protected readonly isOpen$ = this.openState.asReadonly();
  protected readonly heading$ = this.headingState.asReadonly();
  protected readonly description$ = this.descriptionState.asReadonly();
  protected readonly action$ = computed(() =>
    LabActionTools.isVisible(this.actionState()) ? this.actionState() : undefined,
  );
  protected readonly secondaryAction$ = computed(() =>
    LabActionTools.isVisible(this.secondaryActionState()) ? this.secondaryActionState() : undefined,
  );
  protected readonly hasActions$ = computed(
    () => this.action$() !== undefined || this.secondaryAction$() !== undefined,
  );
  protected readonly SolButtonType = SolButtonType;

  protected get resolvedIcon(): SolIcon {
    switch (this.mood) {
      case 'danger':
        return SolIcon.error;
      case 'success':
        return SolIcon.check;
      case 'warning':
        return SolIcon.warning;
      case 'info':
      default:
        return SolIcon.info;
    }
  }

  protected actionButtonMood(action: LabAction): LabButtonMood {
    if (action.mood !== undefined) {
      return action.mood;
    }
    switch (this.mood) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'danger':
        return 'danger';
      case 'info':
      default:
        return 'info';
    }
  }

  protected get resolvedRole(): string {
    return this.mood === 'danger' || this.mood === 'warning' ? 'alert' : 'status';
  }

  protected get dismissIcon(): SolIcon {
    return SolIcon.remove;
  }

  ngOnDestroy(): void {
    this.renderedState.set(false);
    this.openState.set(false);
  }

  protected onActionPress(action: LabAction): void {
    this.actionEmitter.emit(action);
  }

  protected onSecondaryActionPress(action: LabAction): void {
    this.secondaryActionEmitter.emit(action);
  }

  protected onDismissPress(): void {
    this.setOpen(false);
    this.visibleChange.emit(false);
  }

  protected onTransitionEnd(event: TransitionEvent): void {
    if (event.target !== event.currentTarget || event.propertyName !== 'transform' || this.openState()) {
      return;
    }
    this.renderedState.set(false);
  }

  private setOpen(nextOpen: boolean): void {
    if (nextOpen) {
      this.renderedState.set(true);
      // Non-dismissible banners are global state, not a transient message, so
      // they appear immediately and move the shell without a slide animation.
      if (!this.dismissible) {
        this.openState.set(true);
        return;
      }

      this.openState.set(false);
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(() => {
          this.openState.set(true);
        });
      } else {
        this.openState.set(true);
      }
      return;
    }

    this.openState.set(false);
    if (!this.renderedState() || !this.dismissible || this.prefersReducedMotion()) {
      this.renderedState.set(false);
    }
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      'matchMedia' in window &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }
}
